import { GetLoansReturn } from 'graphql/loans/queries';
import { useCreateLoan } from 'hooks/loans/mutations';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import useAlert from 'utils/alert';
import { OrderProperty } from 'types/order';

import { CreateLoan } from '../../types';
import { buildLoanPath, resolveCreatedLoanId } from '../../utils/created-loan';

const createLoanInitialValues: CreateLoan = {
  orderId: '',
  estateId: null,
  loanNumber: null,
};

type Params = {
  onClose: () => void;
  properties: OrderProperty[];
};
export const useCreateLoanModal = ({ onClose, properties }: Params) => {
  const { id: orderId } = useParams<{ id: string }>();
  const showAlert = useAlert();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const createLoanForm = useForm<CreateLoan>({
    defaultValues: createLoanInitialValues,
    mode: 'onChange',
  });

  const { mutate: createModal, isLoading: isCreateLoanLoading } = useCreateLoan({
    queryConfig: {
      onSuccess: (response, variables) => {
        const loans = response.createOrderLoan?.loans || [];
        const previousLoanIds = (queryClient.getQueryData<GetLoansReturn>(['loans', orderId])?.getOrder.loans || [])
          .map((loan) => loan._id);
        const createdLoanId = resolveCreatedLoanId(previousLoanIds, loans, variables.loanNumber);

        // TP-6408: navigate with an absolute path; a relative push from `.../loans`
        // (first loan of a new order) produced an unrouted URL and a blank screen.
        if (createdLoanId) {
          history.push(buildLoanPath(history.location.pathname, createdLoanId));
        }
        queryClient.invalidateQueries(['loans', orderId]);

        showAlert(t('loan:loan-created'), 'success');
        onClose();
      },
    },
  });

  const onConfirm = createLoanForm.handleSubmit((data) => {
    createModal({
      orderId,
      estateId: data.estateId || null,
      loanNumber: data.loanNumber || null,
    });
  });

  useEffect(() => {
    if (properties.length > 0) {
      createLoanForm.reset({
        orderId: '',
        loanNumber: null,
        estateId: properties[0]._id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties]);

  return { createLoanForm, onConfirm, isCreateLoanLoading };
};
